
import { useState, useEffect } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Translation, flattenObject } from '@/utils/translationUtils';
import { toast } from 'sonner';

export const useTranslationManager = () => {
  const { allTranslations, updateTranslation, translate } = useLanguage();
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [filteredTranslations, setFilteredTranslations] = useState<Translation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState<Translation>({ key: '', en: '', no: '' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [paginatedTranslations, setPaginatedTranslations] = useState<Translation[]>([]);

  // Initialize translations when component mounts
  useEffect(() => {
    const flatEnglish = flattenObject(allTranslations.en);
    const flatNorwegian = flattenObject(allTranslations.no);
    
    const translationKeys = Array.from(
      new Set([...Object.keys(flatEnglish), ...Object.keys(flatNorwegian)])
    );
    
    const mappedTranslations = translationKeys.map(key => ({
      key,
      en: flatEnglish[key] || '',
      no: flatNorwegian[key] || ''
    }));
    
    setTranslations(mappedTranslations);
    setFilteredTranslations(mappedTranslations);
  }, [allTranslations]);

  // Filter translations based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTranslations(translations);
    } else {
      const filtered = translations.filter(
        translation =>
          translation.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
          translation.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
          translation.no.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTranslations(filtered);
    }
    // Reset to first page when search changes
    setCurrentPage(1);
  }, [searchQuery, translations]);

  // Update paginated translations when filtered translations or page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedTranslations(filteredTranslations.slice(startIndex, endIndex));
  }, [filteredTranslations, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredTranslations.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Open add dialog
  const handleOpenAddDialog = () => {
    setIsEditMode(false);
    setCurrentTranslation({ key: '', en: '', no: '' });
    setIsDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEditDialog = (translation: Translation) => {
    setIsEditMode(true);
    setCurrentTranslation({ ...translation });
    setIsDialogOpen(true);
  };

  // Handle translation field change
  const handleTranslationChange = (field: keyof Translation, value: string) => {
    setCurrentTranslation(prev => ({ ...prev, [field]: value }));
  };

  // Save translation
  const handleSaveTranslation = () => {
    if (!currentTranslation.key || !currentTranslation.en || !currentTranslation.no) {
      toast.error("All fields are required");
      return;
    }

    try {
      updateTranslation('en', currentTranslation.key, currentTranslation.en);
      updateTranslation('no', currentTranslation.key, currentTranslation.no);
      
      if (isEditMode) {
        toast.success(translate('translations.translationUpdated'));
        
        // Update the translation in our local state
        setTranslations(prev => 
          prev.map(t => 
            t.key === currentTranslation.key ? currentTranslation : t
          )
        );
      } else {
        toast.success(translate('translations.translationAdded'));
        
        // Add new translation to our local state
        setTranslations(prev => [...prev, currentTranslation]);
      }
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving translation:", error);
      toast.error("Error saving translation");
    }
  };

  // Delete translation
  const handleDeleteTranslation = (key: string) => {
    // In a real app, you would remove it from the backend
    // For now, we'll just remove it from our local state
    setTranslations(prev => prev.filter(t => t.key !== key));
    toast.success(translate('translations.translationDeleted'));
  };

  return {
    searchQuery,
    setSearchQuery,
    isDialogOpen,
    setIsDialogOpen,
    isEditMode,
    currentTranslation,
    paginatedTranslations,
    currentPage,
    totalPages,
    handlePageChange,
    handleOpenAddDialog,
    handleOpenEditDialog,
    handleTranslationChange,
    handleSaveTranslation,
    handleDeleteTranslation,
    translate
  };
};
